import React, { useMemo } from 'react';
import { Alert, Card, Col, Row, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link as RouterLink } from 'react-router-dom';
import type { EstimationPerformanceDto } from '@/types/estimationPerformance';

const { Text } = Typography;

function money(sym: string, n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${sym} ${Number(n).toFixed(2)}`;
}

function hours(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${Number(n).toFixed(2)} h`;
}

function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${Number(n).toFixed(1)}%`;
}

export interface EstimationPerformancePanelProps {
  performance: EstimationPerformanceDto | null | undefined;
  currencySymbol?: string;
  /** When true, hide the "link a project" alert (project health already has project). */
  projectScoped?: boolean;
}

/**
 * Cost & time performance: estimate (BOQ Cost Summary / TRE) vs posted project cost
 * and WO operation (stage) hours.
 */
const EstimationPerformancePanel: React.FC<EstimationPerformancePanelProps> = ({
  performance,
  currencySymbol = 'AED',
  projectScoped = false,
}) => {
  const sym = currencySymbol;
  const p = performance;

  const lineColumns: ColumnsType<EstimationPerformanceDto['lines'][number]> = useMemo(
    () => [
      { title: '#', dataIndex: 'line_number', width: 56 },
      {
        title: 'Description',
        dataIndex: 'description',
        ellipsis: true,
        render: (t: string, row) => (
          <span>
            {t}
            {row.line_type ? (
              <Tag style={{ marginLeft: 6 }}>{row.line_type}</Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: 'Est. cost',
        dataIndex: 'estimate_cost',
        width: 110,
        align: 'right',
        render: (v: number) => money(sym, v),
      },
      {
        title: 'Material',
        width: 90,
        align: 'right',
        render: (_, r) => money(sym, r.breakdown?.material),
      },
      {
        title: 'Labor',
        width: 90,
        align: 'right',
        render: (_, r) => money(sym, r.breakdown?.labor),
      },
      {
        title: 'Machine',
        width: 90,
        align: 'right',
        render: (_, r) => money(sym, r.breakdown?.machine),
      },
      {
        title: 'Actual machine',
        dataIndex: 'actual_machine_cost',
        width: 120,
        align: 'right',
        render: (v: number | null) => money(sym, v),
      },
      {
        title: 'Outsourced',
        width: 100,
        align: 'right',
        render: (_, r) => money(sym, r.breakdown?.outsourced),
      },
      {
        title: 'Est. hours',
        dataIndex: 'estimate_hours',
        width: 96,
        align: 'right',
        render: (v: number | null) => hours(v),
      },
      {
        title: 'Actual hours',
        dataIndex: 'actual_hours',
        width: 104,
        align: 'right',
        render: (v: number | null) => hours(v),
      },
    ],
    [sym]
  );

  if (!p || (!p.estimate.cost_total && !p.lines.length && !p.estimate.source_type)) {
    return (
      <Card size="small" title="Cost & time performance" style={{ marginBottom: 16 }}>
        <Text type="secondary">No linked BOQ or Trading & Rental estimation on this order/project.</Text>
      </Card>
    );
  }

  const costVarColor =
    p.variance.cost == null ? undefined : p.variance.cost > 0 ? '#f5222d' : '#52c41a';

  return (
    <Card
      size="small"
      title="Cost & time performance"
      style={{ marginBottom: 16 }}
      extra={
        p.estimate.source_label && p.estimate.link_path ? (
          <RouterLink to={p.estimate.link_path}>{p.estimate.source_label}</RouterLink>
        ) : p.estimate.source_label ? (
          <Text type="secondary">{p.estimate.source_label}</Text>
        ) : null
      }
    >
      {!projectScoped && !p.actual_cost_available ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Actual cost needs a linked project"
          description="Estimates are from BOQ Cost Summary / TRE. Posted project costs appear once this customer order is linked to a project. Actual hours come from work-order operations (stages) on this order."
        />
      ) : null}

      {p.actual_cost_available && p.actual.attribution_method === 'revenue_share' ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Actual cost attributed by revenue share (${pct((p.actual.attribution_share || 0) * 100)})`}
          description={
            p.actual.project_posted_cost_total != null
              ? `Project posted cost ${money(sym, p.actual.project_posted_cost_total)} · this order share ${money(sym, p.actual.cost_total)}`
              : undefined
          }
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Statistic title="Est. cost" value={p.estimate.cost_total} precision={2} prefix={sym} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Actual cost"
            value={p.actual.cost_total ?? undefined}
            precision={2}
            prefix={sym}
            formatter={(v) => (p.actual.cost_total == null ? '—' : String(v))}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Cost variance"
            value={p.variance.cost ?? undefined}
            precision={2}
            prefix={sym}
            valueStyle={{ color: costVarColor }}
            formatter={(v) =>
              p.variance.cost == null
                ? '—'
                : `${p.variance.cost >= 0 ? '+' : ''}${Number(v).toFixed(2)}${
                    p.variance.cost_pct != null ? ` (${pct(p.variance.cost_pct)})` : ''
                  }`
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Est. margin"
            value={p.variance.margin_estimate_pct ?? undefined}
            suffix="%"
            precision={1}
            formatter={(v) => (p.variance.margin_estimate_pct == null ? '—' : String(v))}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Est. hours" valueRender={() => hours(p.estimate.hours_total)} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Actual hours" valueRender={() => hours(p.actual.hours_total)} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Hours variance"
            valueRender={() =>
              p.variance.hours == null
                ? '—'
                : `${p.variance.hours >= 0 ? '+' : ''}${p.variance.hours.toFixed(2)} h${
                    p.variance.hours_pct != null ? ` (${pct(p.variance.hours_pct)})` : ''
                  }`
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Actual margin"
            value={p.variance.margin_actual_pct ?? undefined}
            suffix="%"
            precision={1}
            formatter={(v) => (p.variance.margin_actual_pct == null ? '—' : String(v))}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Est. material"
            value={p.estimate.cost_breakdown.material}
            precision={2}
            prefix={sym}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Actual material"
            value={p.actual.material_total ?? undefined}
            precision={2}
            prefix={sym}
            formatter={(v) => (p.actual.material_total == null ? '—' : String(v))}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title={
              p.actual.committed_source === 'cost_control'
                ? 'Committed (cost control)'
                : p.actual.committed_source === 'po_ordered'
                  ? 'Committed (PO)'
                  : 'Committed'
            }
            value={p.actual.committed_total ?? undefined}
            precision={2}
            prefix={sym}
            formatter={(v) => (p.actual.committed_total == null ? '—' : String(v))}
          />
        </Col>
      </Row>

      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
        Estimate cost matches BOQ Cost Summary (materials + operations). Actual cost = posted project
        costs only. Actual material = issued stock posted as Material on the project. Committed =
        Cost Control when active, else project purchase-order totals (ex-VAT). Actual hours = WO
        stages linked to BOQ ops. Actual machine = actual hours × BOQ machine rate (derived; not
        posted GL). Material is not allocated to BOQ lines.
      </Text>

      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={p.lines}
        columns={lineColumns}
        scroll={{ x: 1200 }}
        expandable={
          p.estimate.source_type === 'boq' || p.lines.some((l) => Boolean(l.operations?.length))
            ? {
                rowExpandable: (row) => Boolean(row.operations?.length),
                expandedRowRender: (row) => (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={row.operations || []}
                    columns={[
                      { title: 'Seq', dataIndex: 'sequence_number', width: 56 },
                      { title: 'Operation', dataIndex: 'description', ellipsis: true },
                      {
                        title: 'Outsourced',
                        dataIndex: 'is_outsourced',
                        width: 100,
                        render: (v: boolean) => (v ? <Tag color="orange">Yes</Tag> : 'No'),
                      },
                      {
                        title: 'Est. hours',
                        dataIndex: 'estimate_hours',
                        width: 100,
                        align: 'right',
                        render: (v: number) => hours(v),
                      },
                      {
                        title: 'Actual hours',
                        dataIndex: 'actual_hours',
                        width: 104,
                        align: 'right',
                        render: (v: number | null) => hours(v),
                      },
                      {
                        title: 'Actual machine',
                        dataIndex: 'actual_machine_cost',
                        width: 120,
                        align: 'right',
                        render: (v: number | null) => money(sym, v),
                      },
                    ]}
                  />
                ),
              }
            : undefined
        }
      />
    </Card>
  );
};

export default EstimationPerformancePanel;
